import { colorStrings, mediaQueriesBP } from "@iewcasia/Utilities/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { safeColor } from "@insite/mobius/utilities";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import parse from "html-react-parser";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";

const enum fields {
    widgetTitle = "widgetTitle",
    sections = "sections",
    sectionTitle = "sectionTitle",
    sectionContentTitle = "sectionContentTitle",
    sectionDescription = "sectionDescription",
    sectionBackgroundImage = "sectionBackgroundImage",
    sectionButtonLabel = "sectionButtonLabel",
    sectionButtonLink = "sectionButtonLink",
    sectionButtonVariant = "sectionButtonVariant",
}

interface SectionModel {
    fields: {
        [fields.sectionTitle]: string;
        [fields.sectionContentTitle]: string;
        [fields.sectionDescription]: string;
        [fields.sectionBackgroundImage]: string;
        [fields.sectionButtonLabel]: string;
        [fields.sectionButtonLink]: LinkFieldValue;
        [fields.sectionButtonVariant]: "primary" | "secondary" | "tertiary";
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.widgetTitle]: string;
        [fields.sections]: SectionModel[];
    };
}

export type Props = HasHistory & OwnProps;

export interface ListAccordionWidgetStyles {
    wrapper?: InjectableCss;
    widgetTitle?: TypographyProps;
    container?: InjectableCss;
    leftRail?: InjectableCss;
    tabList?: InjectableCss;
    tabItem?: InjectableCss;
    tabItemActive?: InjectableCss;
    contentArea?: InjectableCss;
    contentPanel?: InjectableCss;
    contentBackground?: InjectableCss;
    contentOverlay?: InjectableCss;
    contentTitle?: TypographyProps;
    contentDescription?: TypographyProps;
    contentButton?: ButtonProps;
    mobileAccordionContainer?: InjectableCss;
    mobileAccordionItem?: InjectableCss;
    mobileAccordionHeader?: InjectableCss;
    mobileAccordionHeaderActive?: InjectableCss;
    mobileAccordionContent?: InjectableCss;
    mobileAccordionIcon?: InjectableCss;
}

export const listAccordionWidgetStyles: ListAccordionWidgetStyles = {
    wrapper: {
        css: css`
            width: 100%;
            padding: 30px 0;
        `,
    },
    widgetTitle: {
        variant: "h2",
        css: css`
            margin-bottom: 20px;
            text-align: left;
        `,
    },
    container: {
        css: css`
            display: none;

            ${mediaQueriesBP.minLG} {
                display: flex;
                flex-direction: row;
                width: 100%;
                min-height: 450px;
            }
        `,
    },
    leftRail: {
        css: css`
            width: 25%;
            padding-right: 0;
            display: flex;
            flex-direction: column;
        `,
    },
    tabList: {
        css: css`
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
        `,
    },
    tabItem: {
        css: css`
            padding: 15px 20px;
            cursor: pointer;
            font-size: 16px;
            font-weight: 600;
            color: ${getColor(colorStrings.textMain)};
            border-left: 4px solid transparent;
            transition: all 0.2s ease;
            background-color: transparent;

            &:hover {
                background-color: ${getColor(colorStrings.commonGlobalGray04)};
                border-left-color: ${getColor(colorStrings.commonGlobalGray03)};
            }
        `,
    },
    tabItemActive: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalGray04)};
            border-left-color: ${getColor(colorStrings.primaryMain)};
            color: ${getColor(colorStrings.primaryMain)};

            &:hover {
                border-left-color: ${getColor(colorStrings.primaryMain)};
            }
        `,
    },
    contentArea: {
        css: css`
            width: 75%;
            position: relative;
        `,
    },
    contentPanel: {
        css: css`
            position: relative;
            width: 100%;
            height: 100%;
            min-height: 400px;
            border-radius: 8px;
            overflow: hidden;
        `,
    },
    contentBackground: {
        css: css`
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-size: cover;
            background-position: center top;
            background-repeat: no-repeat;
        `,
    },
    contentOverlay: {
        css: css`
            position: absolute;
            top: 50%;
            left: 60px;
            transform: translateY(-50%);
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            padding: 20px 50px;
            border-radius: 8px;
            max-width: 50%;
            min-width: 350px;

            ${mediaQueriesBP.maxLG} {
                max-width: 60%;
                min-width: 280px;
                padding: 30px;
            }
        `,
    },
    contentTitle: {
        variant: "h4",
        weight: 700,
        css: css`
            margin-bottom: 15px;
            font-size: 24px;
        `,
    },
    contentDescription: {
        css: css`
            margin-bottom: 20px;
            font-size: 16px;
            line-height: 1.6;

            p {
                display: -webkit-box;
                -webkit-line-clamp: 7;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }
            ${mediaQueriesBP.maxLG} {
                p {
                    -webkit-line-clamp: 5;
                }
            }
        `,
    },
    contentButton: {
        variant: "primary",
        css: css`
            margin-top: 10px;
        `,
    },
    mobileAccordionContainer: {
        css: css`
            display: block;

            ${mediaQueriesBP.minLG} {
                display: none;
            }
        `,
    },
    mobileAccordionItem: {
        css: css`
            border-bottom: 1px solid ${getColor(colorStrings.commonGlobalGray03)};
        `,
    },
    mobileAccordionHeader: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 15px 20px;
            cursor: pointer;
            font-size: 16px;
            font-weight: 600;
            color: ${getColor(colorStrings.textMain)};
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            transition: all 0.2s ease;

            &:hover {
                background-color: ${getColor(colorStrings.commonGlobalGray04)};
            }
        `,
    },
    mobileAccordionHeaderActive: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalGray04)};
            color: ${getColor(colorStrings.primaryMain)};
        `,
    },
    mobileAccordionContent: {
        css: css`
            overflow: hidden;
            transition: max-height 0.3s ease;
        `,
    },
    mobileAccordionIcon: {
        css: css`
            font-size: 20px;
            transition: transform 0.3s ease;
        `,
    },
};

const styles = listAccordionWidgetStyles;

const TabItem = styled.li<{ isActive: boolean }>`
    padding: 15px 20px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    color: ${props => (props.isActive ? getColor(colorStrings.primaryMain) : getColor(colorStrings.textMain))};
    border-left: 4px solid ${props => (props.isActive ? getColor(colorStrings.primaryMain) : "transparent")};
    transition: all 0.2s ease;
    background-color: ${props => (props.isActive ? getColor(colorStrings.commonGlobalGray04) : "transparent")};

    &:hover {
        background-color: ${getColor(colorStrings.commonGlobalGray04)};
        border-left-color: ${props =>
            props.isActive ? getColor(colorStrings.primaryMain) : getColor(colorStrings.commonGlobalGray03)};
    }
`;

const MobileAccordionHeader = styled.div<{ isActive: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    color: ${props => (props.isActive ? getColor(colorStrings.primaryMain) : getColor(colorStrings.textMain))};
    background-color: ${props =>
        props.isActive ? getColor(colorStrings.commonGlobalGray04) : getColor(colorStrings.commonGlobalWhite)};
    transition: all 0.2s ease;

    &:hover {
        background-color: ${getColor(colorStrings.commonGlobalGray04)};
    }
`;

const MobileAccordionContent = styled.div<{ isOpen: boolean }>`
    max-height: ${props => (props.isOpen ? "800px" : "0")};
    overflow: hidden;
    transition: max-height 0.3s ease;

    ${mediaQueriesBP.maxLG} {
        margin-bottom: ${props => (props.isOpen ? "20px" : "0")};
        margin-top: ${props => (props.isOpen ? "10px" : "0")};
    }
`;

const AccordionIcon = styled.span<{ isOpen: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 1px solid
        ${props =>
            props.isOpen
                ? safeColor(getColor(colorStrings.primaryMain)).rgb().alpha(0.2).toString()
                : safeColor(getColor(colorStrings.textMain)).rgb().alpha(0.2).toString()};
    border-radius: 50%;

    &::before,
    &::after {
        content: "";
        position: absolute;
        background-color: currentColor;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    /* Horizontal line (always visible) */
    &::before {
        width: 10px;
        height: 1.25px;
    }

    /* Vertical line (only for plus icon) */
    &::after {
        width: 1.25px;
        height: 10px;
        display: ${props => (props.isOpen ? "none" : "block")};
    }
`;

const MobileContentPanel = styled.div<{ backgroundImage: string }>`
    position: relative;
    width: 100%;
    min-height: 350px;
    background-image: url("${props => props.backgroundImage}");
    background-size: cover;
    background-position: center top;
    background-repeat: no-repeat;

    ${mediaQueriesBP.maxLG} {
        padding-top: 5px;
        background-image: unset;
        min-height: fit-content;
    }
`;

const MobileContentOverlay = styled.div`
    background-color: ${getColor(colorStrings.commonGlobalGray04)};
    padding: 30px 20px;
    margin: 20px;
    border-radius: 8px;

    ${mediaQueriesBP.maxLG} {
        background-color: ${getColor(colorStrings.commonGlobalWhite)};
        padding: 30px 35px;
        margin: 0;
        border: 1px solid ${getColor(colorStrings.commonGlobalGray03)};
    }
`;

const onClick = (url: string | undefined, isInShell: boolean, history: History) => {
    if (!url) {
        return;
    }

    if (url.startsWith("http")) {
        if (isInShell) {
            return;
        }

        window.location.href = url;
    } else {
        history.push(url);
    }
};

export const ListAccordionWidget = ({ fields, history }: Props) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [mobileOpenIndex, setMobileOpenIndex] = useState(0);  
    const { isInShell } = useShellContext();

    const sectionLinks = useGetLinks(
        (fields.sections ?? []).map(section => section.fields.sectionButtonLink),
        o => o,
    );

    if (!fields.sections || fields.sections.length === 0) {
        return null;
    }

    const activeSection = fields.sections[activeIndex];
    const activeLink = sectionLinks.links[activeIndex]?.url as string;

    const handleMobileToggle = (index: number) => {
        setMobileOpenIndex(mobileOpenIndex === index ? -1 : index);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {fields.widgetTitle && <Typography {...styles.widgetTitle}>{fields.widgetTitle}</Typography>}
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.leftRail}>
                    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                        {fields.sections.map((section, index) => (
                            <TabItem
                                key={index}
                                isActive={activeIndex === index}
                                onClick={() => setActiveIndex(index)}
                                role="tab"
                                aria-selected={activeIndex === index}
                            >
                                {section.fields.sectionTitle}
                            </TabItem>
                        ))}
                    </ul>
                </StyledWrapper>

                <StyledWrapper {...styles.contentArea}>
                    <StyledWrapper {...styles.contentPanel}>
                        <StyledWrapper
                            css={css`
                                ${styles.contentBackground?.css}
                                background-image: url("${activeSection.fields.sectionBackgroundImage}");
                            `}
                        />
                        <StyledWrapper {...styles.contentOverlay}>
                            {activeSection.fields.sectionContentTitle && (
                                <Typography {...styles.contentTitle}>
                                    {activeSection.fields.sectionContentTitle}
                                </Typography>
                            )}
                            {activeSection.fields.sectionDescription && (
                                <Typography {...styles.contentDescription}>
                                    {parse(activeSection.fields.sectionDescription, parserOptions)}
                                </Typography>
                            )}
                            {activeLink && activeSection.fields.sectionButtonLabel && (
                                <Button
                                    {...styles.contentButton}
                                    variant={activeSection.fields.sectionButtonVariant || "primary"}
                                    onClick={() => onClick(activeLink, isInShell ?? false, history)}
                                >
                                    {activeSection.fields.sectionButtonLabel}
                                </Button>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.mobileAccordionContainer}>
                {fields.sections.map((section, index) => {
                    const isOpen = mobileOpenIndex === index;
                    const link = sectionLinks.links[index]?.url as string;

                    return (
                        <StyledWrapper key={index} {...styles.mobileAccordionItem}>
                            <MobileAccordionHeader isActive={isOpen} onClick={() => handleMobileToggle(index)}>
                                <span>{section.fields.sectionTitle}</span>
                                <AccordionIcon isOpen={isOpen} />
                            </MobileAccordionHeader>
                            <MobileAccordionContent isOpen={isOpen}>
                                <MobileContentPanel backgroundImage={section.fields.sectionBackgroundImage}>
                                    <MobileContentOverlay>
                                        {section.fields.sectionDescription && (
                                            <Typography {...styles.contentDescription}>
                                                {parse(section.fields.sectionDescription, parserOptions)}
                                            </Typography>
                                        )}
                                        {link && section.fields.sectionButtonLabel && (
                                            <Button
                                                {...styles.contentButton}
                                                variant={section.fields.sectionButtonVariant || "primary"}
                                                onClick={() => onClick(link, isInShell ?? false, history)}
                                            >
                                                {section.fields.sectionButtonLabel}
                                            </Button>
                                        )}
                                    </MobileContentOverlay>
                                </MobileContentPanel>
                            </MobileAccordionContent>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, null)(withHistory(ListAccordionWidget)) as any,
    definition: {
        displayName: "List Accordion",
        group: "Verndale Box" as any,
        icon: "bars",
        fieldDefinitions: [
            {
                name: fields.widgetTitle,
                displayName: "Widget Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 1,
                isRequired: false,
            },
            {
                name: fields.sections,
                displayName: "Accordion Sections",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: { fields: { sectionTitle?: string } }) => item.fields?.sectionTitle || "Section",
                fieldType: "Contextual",
                validate: (value: unknown[]) =>
                    value.length > 0 ? null : "At least one accordion section is required.",
                fieldDefinitions: [
                    {
                        name: fields.sectionTitle,
                        displayName: "Tab Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 1,
                        isRequired: true,
                    },
                    {
                        name: fields.sectionBackgroundImage,
                        displayName: "Background Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 2,
                        isRequired: true,
                    },
                    {
                        name: fields.sectionContentTitle,
                        displayName: "Content Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 3,
                        isRequired: false,
                    },
                    {
                        name: fields.sectionDescription,
                        displayName: "Content Description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        sortOrder: 4,
                        isRequired: false,
                    },
                    {
                        name: fields.sectionButtonLabel,
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 5,
                        isRequired: false,
                    },
                    {
                        name: fields.sectionButtonLink,
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        sortOrder: 6,
                        isRequired: false,
                    },
                    {
                        name: fields.sectionButtonVariant,
                        displayName: "Button Style",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        defaultValue: "primary",
                        sortOrder: 7,
                        isRequired: false,
                    },
                ],
                sortOrder: 2,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
